/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import RepeatIcon from "@material-ui/icons/Repeat";
import StopIcon from "@material-ui/icons/Stop";
import ScheduleIcon from "@material-ui/icons/Schedule";
import AddIcon from "@material-ui/icons/Add";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import SendIcon from "@material-ui/icons/Send";
import PeopleIcon from "@material-ui/icons/People";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import FilterListIcon from "@material-ui/icons/FilterList";
import BarChartIcon from "@material-ui/icons/BarChart";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import RefreshIcon from "@material-ui/icons/Refresh";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { 
  Grid, FormControl, InputLabel, Select, MenuItem, 
  Typography, Card, CardContent, CardActions, Box,
  LinearProgress, Avatar, Menu, Divider, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  TablePagination
} from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];
    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }
    return [...state, ...newCampaigns];
  }
  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);
    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }
  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderRadius: 0,
  },
  // Dashboard Cards
  dashboardContainer: {
    marginBottom: theme.spacing(3),
  },
  metricCard: {
    borderRadius: 12,
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "100%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    },
  },
  metricIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  metricValue: {
    fontSize: "1.8rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  metricLabel: {
    fontSize: "0.8rem",
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    fontWeight: 500,
  },
  // Filtros
  filtersContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  // Tabs
  tabsContainer: {
    marginBottom: theme.spacing(2),
    borderBottom: "1px solid #e0e0e0",
  },
  // Campaign Cards
  campaignCard: {
    borderRadius: 12,
    marginBottom: theme.spacing(2),
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    border: "1px solid #f0f0f0",
    transition: "box-shadow 0.2s, border-color 0.2s",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      borderColor: "#e0e0e0",
    },
  },
  campaignCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2, 1, 2),
  },
  campaignCardBody: {
    padding: theme.spacing(0, 2, 1, 2),
  },
  campaignCardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2, 2, 2),
    borderTop: "1px solid #f5f5f5",
  },
  campaignName: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#333",
  },
  campaignMeta: {
    fontSize: "0.8rem",
    color: "#888",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  statsRow: {
    display: "flex",
    gap: theme.spacing(3),
    marginTop: theme.spacing(1),
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  statValue: {
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  statLabel: {
    fontSize: "0.7rem",
    color: "#888",
    textTransform: "uppercase",
  },
  // Status chips
  statusChip: {
    fontWeight: 600,
    fontSize: "0.7rem",
    height: 24,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8),
    color: "#999",
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
}));

const getStatusColor = (status) => {
  const colors = {
    "INATIVA": { bg: "#f5f5f5", text: "#666", label: "Inativa" },
    "PROGRAMADA": { bg: "#e3f2fd", text: "#1565c0", label: "Programada" },
    "EM_ANDAMENTO": { bg: "#e8f5e9", text: "#2e7d32", label: "Em Andamento" },
    "CANCELADA": { bg: "#fce4ec", text: "#c62828", label: "Cancelada" },
    "FINALIZADA": { bg: "#f3e5f5", text: "#6a1b9a", label: "Finalizada" },
  };
  return colors[status] || colors["INATIVA"];
};

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { dateToClient } = useDate();
  const { getPlanCompany } = usePlans();

  const [loading, setLoading] = useState(false);
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCampaign, setMenuCampaign] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    delivered: 0,
    pending: 0,
  });
  const [planConfig, setPlanConfig] = useState(false);

  useEffect(() => {
    async function checkPlan() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs);
    }
    checkPlan();
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns", {
        params: { searchParam, status: statusFilter !== "all" ? statusFilter : undefined }
      });
      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records || data });
      
      // Calcular métricas gerais
      const allCampaigns = data.records || data;
      const totalDelivered = allCampaigns.reduce((acc, c) => acc + (c.deliveredMessages || 0), 0);
      const totalPending = allCampaigns.reduce((acc, c) => acc + (c.pendingMessages || 0), 0);
      setStats({
        total: allCampaigns.length,
        active: allCampaigns.filter(c => c.status === "EM_ANDAMENTO").length,
        delivered: totalDelivered,
        pending: totalPending,
      });
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    setSearchParam(e.target.value);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, statusFilter]);

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
    handleMenuClose();
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
    loadCampaigns();
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success("Campanha excluída com sucesso!");
      dispatch({ type: "DELETE_CAMPAIGN", payload: campaignId });
      loadCampaigns();
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
  };

  const handleCancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success("Campanha cancelada!");
      loadCampaigns();
    } catch (err) {
      toastError(err);
    }
    handleMenuClose();
  };

  const handleRestartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success("Campanha reiniciada!");
      loadCampaigns();
    } catch (err) {
      toastError(err);
    }
    handleMenuClose();
  };

  const handleViewReport = (campaign) => {
    history.push(`/campaign-report/${campaign.id}`);
    handleMenuClose();
  };

  const handleMenuOpen = (event, campaign) => {
    setAnchorEl(event.currentTarget);
    setMenuCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCampaign(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const statusMap = ["all", "EM_ANDAMENTO", "PROGRAMADA", "FINALIZADA", "CANCELADA", "INATIVA"];
    setStatusFilter(statusMap[newValue]);
  };

  const getFilteredCampaigns = () => {
    return campaigns;
  };

  const getProgressValue = (campaign) => {
    if (!campaign.shipping || campaign.shipping.length === 0) return 0;
    const delivered = campaign.shipping.filter(s => s.deliveredAt).length;
    const total = campaign.shipping.length;
    return total > 0 ? Math.round((delivered / total) * 100) : 0;
  };

  if (planConfig && !planConfig.plan?.useCampaigns) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Title>Campanhas</Title>
            <Typography variant="body2" color="textSecondary">
              Gerencie suas campanhas de mensagens em massa
            </Typography>
          </Grid>
          <Grid item className={classes.headerActions}>
            <Tooltip title="Atualizar">
              <IconButton onClick={loadCampaigns} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCampaignModal}
              style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}
            >
              Nova Campanha
            </Button>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Dashboard de Métricas */}
        <Grid container spacing={2} className={classes.dashboardContainer}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard} elevation={0}>
              <Box>
                <Typography className={classes.metricValue} style={{ color: "#1976d2" }}>
                  {stats.total}
                </Typography>
                <Typography className={classes.metricLabel}>
                  Total de Campanhas
                </Typography>
              </Box>
              <Box className={classes.metricIcon} style={{ backgroundColor: "#e3f2fd" }}>
                <BarChartIcon style={{ color: "#1976d2" }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard} elevation={0}>
              <Box>
                <Typography className={classes.metricValue} style={{ color: "#2e7d32" }}>
                  {stats.active}
                </Typography>
                <Typography className={classes.metricLabel}>
                  Em Andamento
                </Typography>
              </Box>
              <Box className={classes.metricIcon} style={{ backgroundColor: "#e8f5e9" }}>
                <PlayCircleOutlineIcon style={{ color: "#2e7d32" }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard} elevation={0}>
              <Box>
                <Typography className={classes.metricValue} style={{ color: "#ed6c02" }}>
                  {stats.delivered}
                </Typography>
                <Typography className={classes.metricLabel}>
                  Mensagens Enviadas
                </Typography>
              </Box>
              <Box className={classes.metricIcon} style={{ backgroundColor: "#fff3e0" }}>
                <SendIcon style={{ color: "#ed6c02" }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard} elevation={0}>
              <Box>
                <Typography className={classes.metricValue} style={{ color: "#9c27b0" }}>
                  {stats.pending}
                </Typography>
                <Typography className={classes.metricLabel}>
                  Pendentes
                </Typography>
              </Box>
              <Box className={classes.metricIcon} style={{ backgroundColor: "#f3e5f5" }}>
                <ScheduleIcon style={{ color: "#9c27b0" }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabs de Status */}
        <Box className={classes.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`Todas (${stats.total})`} />
            <Tab label="Em Andamento" />
            <Tab label="Programadas" />
            <Tab label="Finalizadas" />
            <Tab label="Canceladas" />
            <Tab label="Inativas" />
          </Tabs>
        </Box>

        {/* Barra de Busca */}
        <Box className={classes.filtersContainer}>
          <TextField
            placeholder="Buscar campanha..."
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            style={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="disabled" />
                </InputAdornment>
              ),
              style: { borderRadius: 8 }
            }}
          />
        </Box>

        {/* Lista de Campanhas em Cards */}
        {loading ? (
          <Box style={{ padding: 40, textAlign: "center" }}>
            <LinearProgress style={{ borderRadius: 4 }} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              Carregando campanhas...
            </Typography>
          </Box>
        ) : getFilteredCampaigns().length === 0 ? (
          <Box className={classes.emptyState}>
            <SendIcon style={{ fontSize: 64, color: "#ddd", marginBottom: 16 }} />
            <Typography variant="h6" color="textSecondary">
              Nenhuma campanha encontrada
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
              Crie sua primeira campanha para começar a enviar mensagens em massa
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCampaignModal}
              style={{ borderRadius: 8, textTransform: "none" }}
            >
              Criar Campanha
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {getFilteredCampaigns().map((campaign) => {
              const statusInfo = getStatusColor(campaign.status);
              const progress = getProgressValue(campaign);
              return (
                <Grid item xs={12} md={6} lg={4} key={campaign.id}>
                  <Paper className={classes.campaignCard} elevation={0}>
                    {/* Header do Card */}
                    <Box className={classes.campaignCardHeader}>
                      <Box style={{ flex: 1 }}>
                        <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Typography className={classes.campaignName}>
                            {campaign.name}
                          </Typography>
                          {campaign.isRecurring && (
                            <Tooltip title="Campanha recorrente">
                              <RepeatIcon style={{ fontSize: 16, color: "#1976d2" }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Box className={classes.campaignMeta}>
                          {campaign.scheduledAt && (
                            <>
                              <ScheduleIcon style={{ fontSize: 14 }} />
                              {format(new Date(campaign.scheduledAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                            </>
                          )}
                          {!campaign.scheduledAt && campaign.createdAt && (
                            <>
                              <EventAvailableIcon style={{ fontSize: 14 }} />
                              Criada {formatDistanceToNow(new Date(campaign.createdAt), { addSuffix: true, locale: ptBR })}
                            </>
                          )}
                        </Box>
                      </Box>
                      <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Chip
                          label={statusInfo.label}
                          size="small"
                          className={classes.statusChip}
                          style={{ backgroundColor: statusInfo.bg, color: statusInfo.text }}
                        />
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, campaign)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Body do Card */}
                    <Box className={classes.campaignCardBody}>
                      {/* Barra de Progresso */}
                      {(campaign.status === "EM_ANDAMENTO" || campaign.status === "FINALIZADA") && (
                        <>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            className={classes.progressBar}
                            style={{
                              backgroundColor: "#f0f0f0",
                            }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {progress}% concluído
                          </Typography>
                        </>
                      )}

                      {/* Estatísticas rápidas */}
                      <Box className={classes.statsRow}>
                        <Box className={classes.statItem}>
                          <Typography className={classes.statValue} style={{ color: "#2e7d32" }}>
                            {campaign.shipping ? campaign.shipping.filter(s => s.deliveredAt).length : 0}
                          </Typography>
                          <Typography className={classes.statLabel}>Enviados</Typography>
                        </Box>
                        <Box className={classes.statItem}>
                          <Typography className={classes.statValue} style={{ color: "#ed6c02" }}>
                            {campaign.shipping ? campaign.shipping.filter(s => !s.deliveredAt).length : 0}
                          </Typography>
                          <Typography className={classes.statLabel}>Pendentes</Typography>
                        </Box>
                        <Box className={classes.statItem}>
                          <Typography className={classes.statValue} style={{ color: "#1976d2" }}>
                            {campaign.shipping ? campaign.shipping.length : 0}
                          </Typography>
                          <Typography className={classes.statLabel}>Total</Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Footer do Card */}
                    <Box className={classes.campaignCardFooter}>
                      <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {campaign.whatsapp && (
                          <Chip
                            icon={<WhatsAppIcon style={{ fontSize: 14 }} />}
                            label={campaign.whatsapp.name}
                            size="small"
                            variant="outlined"
                            style={{ fontSize: "0.7rem", height: 22 }}
                          />
                        )}
                      </Box>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<BarChartIcon style={{ fontSize: 16 }} />}
                        onClick={() => handleViewReport(campaign)}
                        style={{ textTransform: "none", fontSize: "0.8rem" }}
                      >
                        Relatório
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* Menu de Ações */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ style: { borderRadius: 8, minWidth: 180 } }}
      >
        <MenuItem onClick={() => handleEditCampaign(menuCampaign)}>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => handleViewReport(menuCampaign)}>
          <DescriptionIcon fontSize="small" style={{ marginRight: 8 }} /> Relatório
        </MenuItem>
        <Divider />
        {menuCampaign?.status === "EM_ANDAMENTO" && (
          <MenuItem onClick={() => handleCancelCampaign(menuCampaign)}>
            <StopIcon fontSize="small" style={{ marginRight: 8, color: "#c62828" }} /> 
            <span style={{ color: "#c62828" }}>Parar</span>
          </MenuItem>
        )}
        {(menuCampaign?.status === "CANCELADA" || menuCampaign?.status === "FINALIZADA") && (
          <MenuItem onClick={() => handleRestartCampaign(menuCampaign)}>
            <PlayCircleOutlineIcon fontSize="small" style={{ marginRight: 8, color: "#2e7d32" }} /> 
            <span style={{ color: "#2e7d32" }}>Reiniciar</span>
          </MenuItem>
        )}
        <MenuItem 
          onClick={() => { 
            setDeletingCampaign(menuCampaign); 
            setConfirmModalOpen(true); 
            handleMenuClose(); 
          }}
        >
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8, color: "#c62828" }} /> 
          <span style={{ color: "#c62828" }}>Excluir</span>
        </MenuItem>
      </Menu>

      {/* Modais */}
      <CampaignModal
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        campaignId={selectedCampaign?.id}
      />
      <ConfirmationModal
        title="Excluir Campanha"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingCampaign?.id)}
      >
        Tem certeza que deseja excluir a campanha "{deletingCampaign?.name}"? Esta ação não pode ser desfeita.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Campaigns;
